import React, { useEffect, useState } from "react";
import "./App.css";
import "./helpers/interceptor";
import { useConcurrentLoginPrevention } from "./hooks/useConcurrentLoginPrevention";
import ConcurrentSessionPopup from "./components/base/ConcurrentSessionPopup";

import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import { loginRequest } from "./auth-config";
import Home from "./components/base/Home";
import Login from "./components/base/Login";
import { authenticate } from "./services/user-service";
import withClearCache from "./ClearCache";

const ClearCacheComponent = withClearCache(MainApp);

function App() {
  return <ClearCacheComponent />;
}

function MainApp() {
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const [validUser, setValidUser] = useState();
  const [idToken, setIdToken] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [userData, setUserData] = useState(null);
  const [showConcurrentPopup, setShowConcurrentPopup] = useState(false);

  // Apply concurrent login prevention
  useConcurrentLoginPrevention(userData);

  useEffect(() => {
    console.log('useEffect triggered - isAuthenticated:', isAuthenticated, 'idToken:', idToken);
    if (isAuthenticated) {
      RequestProfileData();
    }
    if (idToken && idToken !== "") {
      console.log('idToken exists, calling getUserData');
      sessionStorage.setItem("Token", idToken);
      getUserData();
    }
  }, [isAuthenticated, idToken]); // eslint-disable-line react-hooks/exhaustive-deps

  async function RequestProfileData() {
    console.log('RequestProfileData called');
    await instance.initialize();
    const accounts = await instance.getAllAccounts();
    console.log("accounts", accounts);
    if (accounts.length > 0) {
      const request = {
        ...loginRequest,
        account: accounts[0],
      };
      // Silently acquires an access token which is then attached to a request for Microsoft Graph data
      instance
        .acquireTokenSilent(request)
        .then((response) => {
          console.log('Token acquired silently:', response.idToken);
          setIdToken(response.idToken);
        })
        .catch((e) => {
          console.log('Silent token failed, trying popup:', e);
          instance.acquireTokenPopup(request).then((response) => {
            console.log('Token acquired via popup:', response.idToken);
            setIdToken(response.idToken);
          });
        });
    } else {
      console.log('No accounts found');
      setValidUser(false);
      setErrorMessage("Unable to login into system.");
    }
  }
  async function getUserData(forceLogin = false) {
    console.log('Inside getUserData');
    console.log('getUserData called with forceLogin:', forceLogin);
    let result = await authenticate(forceLogin);
    
    console.log('getUserData result:', result);
    
    if (result?.hasActiveSession && !forceLogin) {
      console.log('Showing concurrent popup');
      setShowConcurrentPopup(true);
      setValidUser(false);
      return;
    }
    
    if (result?.success === false) {
      console.log('Authentication failed:', result.error);
      setValidUser(false);
      setErrorMessage(result.error || 'Authentication failed');
      return;
    }
    
    if (result?.UserData) {
      let userData = result.UserData;
      sessionStorage.setItem("UserName", userData.userName || "");
      sessionStorage.setItem("Role", userData.roleCode || "");
      sessionStorage.setItem("Token", result.Token);
      sessionStorage.setItem("SessionId", result.SessionId);
      sessionStorage.setItem("Email", userData.userId || "");
      sessionStorage.setItem(
        "AuthorizedSessions",
        userData.authorizedSessions ? JSON.stringify(userData.authorizedSessions) : []
      );
      setUserData({ UserId: userData.userId, UserName: userData.userName });
      setValidUser(true);
      setShowConcurrentPopup(false);
    } else {
      console.log('No UserData in result');
      setValidUser(false);
    }
  }

  const handleContinueSession = () => {
    getUserData(true);
  };

  const handleCancelSession = () => {
    setShowConcurrentPopup(false);
    sessionStorage.clear();
    instance.logoutRedirect();
  };

  return (
    <React.Fragment>
      {showConcurrentPopup && (
        <ConcurrentSessionPopup
          onContinue={handleContinueSession}
          onCancel={handleCancelSession}
        />
      )}
      <AuthenticatedTemplate>
        {validUser === true && <Home />}
        {validUser === false && <Login errorMessage={errorMessage} />}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
}

export default App;
